import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {GeoRefAddress} from "@reva/shared/geo-reference";
import {type AvmSpecSegment, SEGMENT} from "@reva/shared/reva-spec";
import {segmentFromBuildingType} from "@reva/shared/segment-description";

import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

interface ImportState {
    /** Target URL for Immodrop */
    immodropUrl: string | null;
    /** Filename for Immodrop, when the query was started via file upload instead of a target URL */
    immodropFilename: string | null;
    /** Id of the uploaded exposé PDF in the Orders API, when archiving it there succeeded (#19159) */
    immodropDocumentId: string | null;
    /** Is the ImmoDrop request currently loading? */
    immodropLoading: boolean;
    /** The current query error from the last Immodrop request */
    immodropQueryError: ApiQueryError | null;

    /** Entered JSON input text from user */
    jsonInput: string | null;
    /** The current parsing error (i18n key) from the last JSON import request */
    jsonParsingErrorI18n: string | null;

    /** The address query for the import (extracted from either Immodrop or the importJson) */
    addressQuery: string | null;
    /** Is the Address geo-referencing currently loading? */
    addressLoading: boolean;
    /** The current query error from geo-referencing the importAddress */
    addressError: ApiQueryError | null;

    /** JSON object, containing the payload (in AVM-API format) to be imported. */
    importJson: Record<string, unknown> | null;
    /** Segment to be imported */
    importSegment: AvmSpecSegment | null;
    /** object parameters to be imported */
    importObjectParams: Record<string, unknown> | null;
    /** the found address meta to be imported */
    importAddress: GeoRefAddress | null;
}

const initialState: ImportState = {
    immodropUrl: null,
    immodropFilename: null,
    immodropDocumentId: null,
    immodropLoading: false,
    immodropQueryError: null,

    jsonInput: null,
    jsonParsingErrorI18n: null,

    addressQuery: null,
    addressLoading: false,
    addressError: null,

    importJson: null,
    importSegment: null,
    importObjectParams: null,
    importAddress: null,
};

export const importSlice = createSlice({
    name: "import",
    initialState,
    reducers: {
        clear: (state) => {
            state.immodropUrl = null;
            state.immodropFilename = null;
            state.immodropDocumentId = null;
            state.immodropLoading = false;
            state.immodropQueryError = null;

            state.jsonInput = null;
            state.jsonParsingErrorI18n = null;

            state.addressQuery = null;
            state.addressLoading = false;
            state.addressError = null;

            state.importJson = null;
            state.importSegment = null;
            state.importObjectParams = null;
            state.importAddress = null;
        },
        setImmodropLoading: (
            state,
            action: PayloadAction<{targetUrl: string | null; targetFilename?: string | null}>,
        ) => {
            const {targetUrl, targetFilename} = action.payload;

            state.immodropUrl = targetUrl;
            state.immodropFilename = targetFilename ?? null;
            state.immodropDocumentId = null;
            state.immodropLoading = true;
            state.immodropQueryError = null;

            state.addressQuery = null;
            state.addressLoading = false;
            state.addressError = null;

            state.importJson = null;
            state.importSegment = null;
            state.importObjectParams = null;
            state.importAddress = null;
        },
        setImmodropError: (state, action: PayloadAction<QueryError>) => {
            state.immodropLoading = false;
            state.immodropQueryError = buildApiQueryError(action.payload);
        },
        setJsonInput: (state, action: PayloadAction<{jsonInput: string}>) => {
            const {jsonInput} = action.payload;

            state.jsonInput = jsonInput;
            state.jsonParsingErrorI18n = null;

            state.addressQuery = null;
            state.addressLoading = false;
            state.addressError = null;

            state.importJson = null;
            state.importSegment = null;
            state.importObjectParams = null;
            state.importAddress = null;
        },
        setJsonParsingErrorI18n: (state, action: PayloadAction<{errorI18n: string}>) => {
            const {errorI18n} = action.payload;

            state.jsonParsingErrorI18n = errorI18n;
        },
        setImportJsonData: (state, action: PayloadAction<{importJson: Record<string, unknown>}>) => {
            const {importJson} = action.payload;

            const source: Record<string, unknown> = importJson ?? {};
            const {address, segment, ...params} = source;
            state.immodropLoading = false;

            state.addressQuery = (address as string) || "";
            state.addressLoading = false;
            state.addressError = null;

            state.importJson = importJson;
            // Die Extraktion liefert nicht für jedes Exposé ein Segment, z.B. nicht für Mehrfamilienhäuser (#19159).
            state.importSegment = (segment as AvmSpecSegment) || segmentFromBuildingType(params.building_type);
            state.importObjectParams = params;
            state.importAddress = null;
        },
        setImmodropDocumentId: (state, action: PayloadAction<{documentId: string | null}>) => {
            state.immodropDocumentId = action.payload.documentId;
        },
        setSegment: (state, action: PayloadAction<{segment: AvmSpecSegment}>) => {
            const {segment} = action.payload;

            state.importSegment = segment;
        },
        setAddressLoading: (state, action: PayloadAction<{addressQuery: string}>) => {
            const {addressQuery} = action.payload;
            state.addressQuery = addressQuery || "";
            state.addressLoading = true;
            state.addressError = null;

            state.importAddress = null;
        },
        setAddressError: (state, action: PayloadAction<QueryError>) => {
            state.addressLoading = false;
            state.addressError = buildApiQueryError(action.payload);

            state.importAddress = null;
        },
        setAddressFound: (state, action: PayloadAction<{addressMeta: GeoRefAddress}>) => {
            const {addressMeta} = action.payload;
            state.addressLoading = false;
            state.addressError = null;

            state.importAddress = addressMeta;
        },
    },
    selectors: {
        selectImmodropUrl: (state) => state.immodropUrl,
        selectImmodropFilename: (state) => state.immodropFilename,
        selectImmodropDocumentId: (state) => state.immodropDocumentId,
        selectImmodropLoading: (state) => state.immodropLoading,
        selectImmodropQueryError: (state) => state.immodropQueryError,

        selectJsonInput: (state) => state.jsonInput,
        selectJsonParsingErrorI18n: (state) => state.jsonParsingErrorI18n,

        selectAddressQuery: (state) => state.addressQuery,
        selectAddressLoading: (state) => state.addressLoading,
        selectAddressError: (state) => state.addressError,

        selectImportJson: (state) => state.importJson,
        selectImportSegment: (state) => state.importSegment,
        selectImportObjectParams: (state) => state.importObjectParams,
        selectImportAddress: (state) => state.importAddress,

        selectValid: (state) => {
            return (
                !!state.importJson &&
                !!state.importAddress &&
                !!state.importObjectParams &&
                !!state.importSegment &&
                (
                    [SEGMENT.WHG_K, SEGMENT.EZFH_K, SEGMENT.MFHWGH_K, SEGMENT.GRST_K, SEGMENT.WHG_M] as AvmSpecSegment[]
                ).includes(state.importSegment)
            );
        },
    },
});

export const {
    clear,
    setImmodropLoading,
    setImmodropError,
    setImmodropDocumentId,
    setJsonInput,
    setJsonParsingErrorI18n,
    setImportJsonData,
    setSegment,
    setAddressLoading,
    setAddressError,
    setAddressFound,
} = importSlice.actions;

export const {
    selectImmodropUrl,
    selectImmodropFilename,
    selectImmodropDocumentId,
    selectImmodropLoading,
    selectImmodropQueryError,
    selectJsonInput,
    selectJsonParsingErrorI18n,
    selectAddressQuery,
    selectAddressLoading,
    selectAddressError,
    selectImportJson,
    selectImportSegment,
    selectImportObjectParams,
    selectImportAddress,
    selectValid,
} = importSlice.selectors;

export default importSlice.reducer;
